import { fabric } from 'fabric';
import { buildQrPath, QrInput, QrType } from '../../../utils/qrCode';

// Shown at this many slide pixels when added.
export const QR_DEFAULT_SIZE = 360;
const QR_PATH_SIZE = 100;

export interface QrSettings {
  type: QrType;
  input: QrInput;
  content: string;
  background: boolean;
}

export const isQrCode = (object?: fabric.Object | null) =>
  object?.data?.type === 'qr';

// A stretched code is harder to scan: only the corner handles, which scale
// both sides together. Control visibility is not saved, so this is applied
// again whenever a QR code is loaded or pasted.
export const applyQrControls = (object: fabric.Object) => {
  if (!isQrCode(object)) return;
  object.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false });
};

// Stored as a path so players draw it like any shape (from data.svgPath).
export const createQrObject = (
  settings: QrSettings,
  options: fabric.IPathOptions = {},
) => {
  const svgPath = buildQrPath(settings.content);
  const object = new fabric.Path(svgPath, {
    fill: '#000000',
    // No stroke, so the box (and background) is exactly the 100x100 grid
    // the players draw.
    strokeWidth: 0,
    ...options,
    backgroundColor: settings.background ? '#ffffff' : '',
    data: {
      ...(options.data || {}),
      type: 'qr',
      qrType: settings.type,
      qrInput: settings.input,
      qrContent: settings.content,
      svgPath,
      fillRule: 'nonzero',
    },
  });
  applyQrControls(object);
  return object;
};

export const createQrObjectForSlide = (
  canvas: fabric.Canvas,
  settings: QrSettings,
) => {
  const zoom = canvas.getZoom() || 1;
  const slideWidth = canvas.getWidth() / zoom;
  const slideHeight = canvas.getHeight() / zoom;
  const size = Math.min(QR_DEFAULT_SIZE, slideWidth / 2, slideHeight / 2);
  const scale = size / QR_PATH_SIZE;
  return createQrObject(settings, {
    left: (slideWidth - size) / 2,
    top: (slideHeight - size) / 2,
    scaleX: scale,
    scaleY: scale,
  });
};

// A new path replaces the old one at the same place in the stacking order.
// Every QR path spans the same 100x100 box, so position and size carry over.
export const replaceQrObject = (
  canvas: fabric.Canvas,
  object: fabric.Object,
  settings: QrSettings,
) => {
  const replacement = createQrObject(settings, {
    left: object.left,
    top: object.top,
    scaleX: object.scaleX,
    scaleY: object.scaleY,
    angle: object.angle,
    flipX: object.flipX,
    flipY: object.flipY,
    opacity: object.opacity,
    fill: object.fill as string,
    lockMovementX: object.lockMovementX,
    lockMovementY: object.lockMovementY,
    lockRotation: object.lockRotation,
    lockScalingX: object.lockScalingX,
    lockScalingY: object.lockScalingY,
    data: { name: object.data?.name },
  });
  // @ts-ignore: the replacement is recorded once by the caller.
  replacement.auto = true;
  canvas.insertAt(replacement, canvas.getObjects().indexOf(object), false);
  canvas.setActiveObject(replacement);
  canvas.remove(object);
  canvas.requestRenderAll();
  return replacement;
};
